import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ANIMATION_DURATION,
  DEFAULT_MAX_HEIGHT,
  MIN_AVAILABLE_HEIGHT,
  VIRTUALIZATION_THRESHOLD,
} from '../constants'
import type { UseDropdownUIProps } from '../types'

export function useDropdownUI(props: UseDropdownUIProps) {
  // State management
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [showOnlySelected, setShowOnlySelected] = useState(false)

  // Animation state for smooth text transitions
  const [animationKey, setAnimationKey] = useState(0)
  const [tempModeText, setTempModeText] = useState<string>('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Refs for measuring element heights
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const selectionHeaderRef = useRef<HTMLDivElement>(null)
  const searchFooterRef = useRef<HTMLDivElement>(null)
  const parentRef = useRef<HTMLDivElement | null>(null)

  // State for measured heights
  const [elementHeights, setElementHeights] = useState({
    searchBox: 0,
    selectionHeader: 0,
    searchFooter: 0,
  })

  // Memoize filtered options
  const filteredOptions = useMemo(() => {
    let options = props.data.options ?? []

    // Filter by search if searchable
    if (props.visibility.searchable && search) {
      const separator = props.visibility.searchTermSeparator
      const terms = (separator ? search.split(separator) : [search])
        .map((term) => term.trim().toLowerCase())
        .filter(Boolean)

      // Every term must match somewhere; a single term is the plain substring search.
      options = options.filter((option) =>
        terms.every((term) => {
          const matchesLabel = option.label.toLowerCase().includes(term)
          const matchesValue = String(option.value).toLowerCase().includes(term)
          const matchesKeywords = option.keywords?.some((keyword) =>
            keyword.toLowerCase().includes(term),
          )
          return matchesLabel || matchesValue || matchesKeywords
        }),
      )
    }

    // Filter by selection if showing only selected
    if (showOnlySelected && props.layout.isMultiSelect) {
      options = options.filter((option) => props.data.selectedOptions.includes(option.value))
    }

    return options
  }, [
    props.data.options,
    props.visibility.searchable,
    props.visibility.searchTermSeparator,
    search,
    showOnlySelected,
    props.layout.isMultiSelect,
    props.data.selectedOptions,
  ])

  // Determine if virtualization should be used
  const shouldVirtualize = useMemo(() => {
    return filteredOptions.length > VIRTUALIZATION_THRESHOLD
  }, [filteredOptions.length])

  // Memoize display mode toggle handler
  const handleDisplayModeToggle = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    const newMode = !showOnlySelected
    const modeText = newMode ? 'Selected Only' : 'All'

    // Show the mode text briefly
    setTempModeText(modeText)
    setAnimationKey((prev) => prev + 1)

    // Update the actual mode
    setShowOnlySelected(newMode)

    // Clear the temporary text after animation
    timeoutRef.current = setTimeout(() => {
      setTempModeText('')
      setAnimationKey((prev) => prev + 1)
      timeoutRef.current = null
    }, ANIMATION_DURATION)
  }, [showOnlySelected])

  // Memoize search clear handler
  const handleSearchClear = useCallback(() => {
    setSearch('')
  }, [])

  // Reset to showing all options when clearing
  const resetShowOnlySelected = useCallback(() => {
    setShowOnlySelected(false)
  }, [])

  // Memoize height calculation function
  const calculateAvailableHeight = useCallback(() => {
    const totalHeight = props.layout.maxHeight || DEFAULT_MAX_HEIGHT
    let availableHeight = totalHeight

    // Subtract search box height if visible
    if (props.visibility.searchable) {
      availableHeight -= elementHeights.searchBox
    }

    // Subtract selection header height if visible
    if (
      !props.visibility.hideSelectionHeader &&
      props.layout.isMultiSelect &&
      props.layout.selectedOptionsLength > 0
    ) {
      availableHeight -= elementHeights.selectionHeader
    }

    // Subtract search footer height if visible
    if (!props.visibility.hideSearchFooter && search) {
      availableHeight -= elementHeights.searchFooter
    }

    return `${Math.max(availableHeight, MIN_AVAILABLE_HEIGHT)}px`
  }, [
    props.layout.maxHeight,
    props.visibility.searchable,
    props.visibility.hideSelectionHeader,
    props.layout.isMultiSelect,
    props.layout.selectedOptionsLength,
    props.visibility.hideSearchFooter,
    elementHeights,
    search,
  ])

  // Measure element heights when they change using useLayoutEffect for better performance
  useLayoutEffect(() => {
    const measureHeights = () => {
      setElementHeights({
        searchBox: searchBoxRef.current?.offsetHeight || 0,
        selectionHeader: selectionHeaderRef.current?.offsetHeight || 0,
        searchFooter: searchFooterRef.current?.offsetHeight || 0,
      })
    }

    measureHeights()
  }, [
    props.visibility.searchable,
    props.visibility.hideSelectionHeader,
    props.layout.isMultiSelect,
    props.layout.selectedOptionsLength,
    props.visibility.hideSearchFooter,
  ])

  return {
    // State
    isOpen,
    setIsOpen,
    search,
    setSearch,
    showOnlySelected,
    setShowOnlySelected,
    animationKey,
    tempModeText,

    // Refs
    searchBoxRef,
    selectionHeaderRef,
    searchFooterRef,
    parentRef,

    // Handlers
    handleDisplayModeToggle,
    handleSearchClear,
    resetShowOnlySelected,

    // Computed values
    calculateAvailableHeight,
    filteredOptions,
    shouldVirtualize,
  }
}
